// A family's account: what the ledger screens decide.
//
// **Adapters.** The rules are `core/src/account.rs`, so the Android and iOS
// family screens offer the same kinds, refuse the same figures, write the same
// entry and say the same sentences as these; `core/src/family_money_vectors.json`
// holds the recorded answers. What stays here is the coercion — a form's loose
// values into the core's typed arguments, and the core's rows back into a
// document.
//
// Money a sentence quotes is handed in already written out, because
// formatting money is the studio's locale and not a rule.

import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';
import { tombstoneOf } from './tombstone.js';

const text = (v) => (v == null ? null : String(v));

/**
 * A ledger entry the rules would refuse, refused before anything is queued:
 * the core's code and its sentence. Without it a missing family, a negative
 * amount and one past the ceiling would be queued and refused by
 * `validLedgerEntry` after the drawer had closed.
 */
export class LedgerEntryRefused extends Error {
  constructor(refusal) {
    super(refusal.message);
    this.name = 'LedgerEntryRefused';
    this.code = refusal.code;
  }
}

// ── The kind picker ────────────────────────────────────────────────────────

/**
 * The five ledger primitives, each introduced by what it is *for* rather than
 * by its effect on the balance — MMS's framing; see TransactionTypePicker.jsx.
 * Asked lazily, because `loadCore()` is awaited after collection (#226).
 */
export const transactionTypes = () => coreSync().transactionTypes();

/** The kind's label; `undefined` for an unknown kind. */
export const kindLabel = (id) => coreSync().transactionKindLabel(text(id)) ?? undefined;

/** The Add Transaction drawer's title: the kind's, once one is picked. */
export const transactionDrawerTitle = (kind) => coreSync().transactionDrawerTitle(text(kind));

// ── The form ───────────────────────────────────────────────────────────────

/** A blank entry, dated today. */
export const emptyTransactionForm = (today) => coreSync().emptyTransactionForm(today);

/**
 * What Save & Add Another leaves in the form: the student, the date and the
 * receipt tick stay; the amount and the description are the next entry's.
 */
export const transactionFormAfterAddAnother = (form) => ({
  ...form,
  ...coreSync().transactionFormAfterAddAnother({
    studentId: String(form.studentId ?? ''),
    date: String(form.date ?? ''),
    amount: String(form.amount ?? ''),
    description: String(form.description ?? ''),
    emailReceipt: Boolean(form.emailReceipt),
  }),
});

/**
 * What the drawer shows under Amount and Family, and whether Save refuses.
 *
 * Save is never disabled (#180): it refuses at submit, and a required field
 * says so only once Save has been pressed (`attempted`). An amount past the
 * ceiling says so at once. `choosesFamily` is the drawer opened without a
 * family, which asks for one.
 */
export function transactionFormErrors({ amount, familyId, choosesFamily, attempted }, currency) {
  const e = coreSync().transactionFormErrors(
    text(amount), text(familyId) || null, Boolean(choosesFamily), Boolean(attempted), currency,
  );
  return { amount: e.amountMessage ?? null, family: e.familyMessage ?? null, refused: e.refused };
}

/**
 * The Add Transaction form read into the entry it writes: the amount typed in
 * the studio's currency becomes minor units, once; an unpicked student is
 * `null` and an empty description `''`.
 */
export function transactionInput({
  kind, amount, date, familyId, studentId, description,
}, currency) {
  const i = coreSync().transactionInput(
    text(kind), text(amount), text(date), text(familyId), text(studentId), text(description), currency,
  );
  return {
    kind: i.kind,
    amountMinor: i.amountMinor,
    occurredOn: i.occurredOn,
    familyId: i.familyId ?? null,
    studentId: i.studentId ?? null,
    description: i.description,
  };
}

/**
 * The ledger document a hand-entered transaction writes. `input.id` is the
 * caller's — a random one (`freeId`), because two identical cash payments on
 * one day are two payments. Throws `LedgerEntryRefused` for an entry the
 * rules would refuse.
 */
export function transactionDocument(input, serverTimestamp) {
  const crossing = {
    kind: String(input.kind ?? ''),
    amountMinor: Number(input.amountMinor),
    occurredOn: String(input.occurredOn ?? ''),
    familyId: text(input.familyId),
    studentId: text(input.studentId),
    description: String(input.description ?? ''),
  };
  const refusal = coreSync().transactionRefusal(crossing);
  if (refusal) throw new LedgerEntryRefused(refusal);
  return documentFromRows(coreSync().transactionDocument(String(input.id), crossing), serverTimestamp);
}

/**
 * Whether saving this entry asks for a payment receipt: a payment, with
 * `Send an email receipt` ticked, that was written. The message itself is
 * queued against the entry's id, so recording the same payment twice asks
 * for one receipt.
 */
export function receiptRequested({ kind, emailReceipt, entryId }) {
  return coreSync().receiptRequested(text(kind), Boolean(emailReceipt), text(entryId) || null);
}

/** The Family picker's order: positions, by the family's display name. */
export const familyPickerOrder = (labels) => [...coreSync().labelOrder(labels.map(String))];

// ── Delete Transaction ─────────────────────────────────────────────────────

// Which ledger rows offer "Delete Transaction" (#191): the family ledger, the
// cross-family Transactions tab and the phones all ask this one rule.
//
// MMS's Transactions row offers "Edit Transaction" / "Delete Transaction" on a
// hand-entered charge, payment, discount or refund (#188). Deleting there
// removes the row outright: no struck-through remainder, no "voided" badge,
// and the confirmation names the amount and date but never asks for a reason.
//
// A row generated from attendance ('attendance') or a lesson ('lesson') gets no
// such action — MMS's kebab on that kind of row offers only "Edit Attendance" /
// "Edit Lesson Details" — because retracting it has to go through whatever
// produced it (setAttendance's rewrite, or the #157 delete-lesson dialog) to
// keep the derived charge ID in step with the record that derives it. A lesson
// delete always retracts its billing, so such a charge never outlives its
// lesson (#193).
//
// ── Where we knowingly differ (#191) ───────────────────────────────────────
//
// **MMS deletes a package-sourced charge from the Transactions tab**, and this
// refuses to. MMS's confirm says it "will remove the charge **and the package
// time** from the student's account": the two are retracted together there.
// `voidEntry` tombstones the ledger row and nothing else (#188), so admitting
// `sourceType === 'package'` would leave the student holding time nobody paid
// for. The model difference behind that is #181.
//
// The mechanism differs too, not what the teacher sees: a hard delete of a
// synced document can resurrect from a disconnected device's cache (see
// useLedger.jsx), so `voidEntry` tombstones instead and the fold skips a
// voided entry — the row disappears from the table as it does in MMS.
export const canVoidEntry = (e) => coreSync().canVoidEntry(text(e?.sourceType) || null);

/** The merging write that deletes one — a tombstone, both halves (#70). */
export const voidEntryDocument = (serverTimestamp) => documentFromRows(
  coreSync().voidEntryDocument(), serverTimestamp,
);

// The confirm's sentence, shared so the two tables cannot drift apart on it.
//
// MMS's own, from the Transactions tab:
//
//   > Are you sure you want to delete the ($50.99)discount for <family name>
//   > on 9/16/2026?
//
// (The missing space is MMS's and is not reproduced.) It names the amount, the
// kind, the family and the date; the family matters on the cross-family list,
// where two families' rows sit next to each other.
//
// The amount and the date arrive written out, because they are the studio's
// locale: `amountText` is the entry's amount, `dateText` its date in the
// studio's pattern — an empty one falls back to the stored date. The kind's
// label is the core's table.
export function voidConfirmMessage(entry, { familyName, amountText, dateText }) {
  if (!entry) return '';
  return coreSync().voidConfirmMessage(
    String(entry.kind ?? ''), String(entry.occurredOn ?? ''), String(familyName ?? ''),
    String(amountText ?? ''), String(dateText ?? ''),
  );
}

// ── The family ledger ──────────────────────────────────────────────────────

/**
 * How one row of the family's ledger reads: which money column the amount
 * sits in — `charges` for MMS's Charges & Discounts, `payments` for Payments
 * & Refunds (#643) — what the Description cell says, whether the Date carries
 * the `Lesson` chip, and the pill the amount is drawn in: its `label`
 * (`Payment`) and `tone` (`owe` raises the balance, `credit` lowers it,
 * #635). The Transactions tab draws the same pill.
 */
export function ledgerRowView(e) {
  return coreSync().ledgerRowView(String(e?.kind ?? ''), text(e?.description), text(e?.sourceType));
}

/**
 * The figure an entry's pill writes, signed as it counts in its column
 * (#649): a discount and a refund take from theirs, so they are negative and
 * `formatMoneyInParentheses` writes them MMS's way, `Discount ($10.00)`,
 * `Refund ($5.00)`. The Delete Transaction sentence writes the same figure.
 */
export function ledgerPillMinor(e) {
  return ledgerRowView(e).sign * (Math.round(Number(e?.amountMinor)) || 0);
}

/**
 * A balance read three ways: they owe you, you owe them (a credit, shown as
 * its magnitude with ` credit`), or settled.
 */
export const balanceView = (minor) => coreSync().balanceView(minor);

/** The balances handed in, summed, a credit as none. */
export const totalOwed = (balances) => coreSync().totalOwed(balances);

/**
 * "You're owed" across the studio (#640): the balance of each family in
 * `familyIds` — the families the studio has, the rows of Family Accounts —
 * folded from `byFamily`, the ledger grouped by `familyId`. A group whose
 * family is not listed is not counted, so the header is the sum of the rows
 * it sits over; the rule and why are `account::owed_by_families` in the core.
 */
export function owedByFamilies(familyIds, byFamily, balanceOf) {
  const ids = [...byFamily.keys()].map((id) => (id == null ? '' : String(id)));
  const balances = [...byFamily.values()].map((list) => Number(balanceOf(list)) || 0);
  return coreSync().owedByFamilies(familyIds.map(String), ids, balances);
}

/** The current month as the pair the date boxes open at. */
export const monthRange = (today) => coreSync().monthRange(today);

// ── The Transactions tab and Charge Categories ─────────────────────────────

/** A ledger document as the core's `AccountEntry`. */
function accountEntry(e) {
  const { voided, voidedAt } = tombstoneOf(e);
  return {
    id: String(e?.id ?? ''),
    kind: String(e?.kind ?? ''),
    amountMinor: Math.round(Number(e?.amountMinor) || 0),
    occurredOn: String(e?.occurredOn ?? ''),
    sourceType: text(e?.sourceType),
    voided,
    voidedAt: voidedAt || null,
  };
}

/**
 * Every live entry inside the range, newest first — by business date, then
 * by id, the ledger's own order reversed, so two entries on one day do not
 * come out in whatever order the snapshot delivered them.
 */
export function transactionsInRange(entries, range) {
  const order = coreSync().transactionsInRange(entries.map(accountEntry), range.start, range.end);
  return [...order].map((i) => entries[i]);
}

/**
 * The breakdown the Charge Categories drawer shows: per kind, in the core's
 * order, how many live entries, their total, and how many a lesson generated.
 * Kinds with none are left out.
 */
export function chargeCategoryRows(entries) {
  return coreSync().chargeCategoryRows(entries.map(accountEntry)).map((r) => ({
    kind: r.kind, label: r.label, count: r.count, total: r.totalMinor, generated: r.generated,
  }));
}
